import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import SportsScoreIcon from "@mui/icons-material/SportsScore";
import ConnectWithoutContactIcon from "@mui/icons-material/ConnectWithoutContact";
import ReplayIcon from "@mui/icons-material/Replay";
import LoadSpinner from "../../../utilities/LoadSpinner";
import RoleplayScorecard from "./RoleplayScorecard";
import RoleplayRealtimeService, {
  ConnectionState as VoiceConnectionState,
  ActivityState as VoiceActivityState,
} from "./roleplayRealtimeService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getRightworkAPI, postRightworkAPI } from "../requests/utilities/requests";
import { streamRightworkAPI } from "../requests/utilities/streamingRequests";
import {
  API_post_learning_roleplay_attempt_start,
  API_post_learning_roleplay_turn_stream,
  API_post_learning_roleplay_attempt_complete,
  API_get_learning_roleplay_attempt,
  API_post_learning_roleplay_preview_turn_stream,
  API_post_learning_roleplay_preview_grade,
  API_post_learning_roleplay_voice_session,
  API_post_learning_roleplay_voice_preview_session,
  API_post_learning_roleplay_voice_turns,
} from "../../../constants";

const style = {
  position: "absolute",
  inset: 0,
  margin: "auto",
  width: "94%",
  height: "92%",
  maxWidth: "640px",
  maxHeight: "92vh",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  border: "none",
};

// Chat mechanics mirror AssistantDisplay.js: a `messages` array, an isTyping
// indicator, and a streaming handler that appends a placeholder AI bubble on
// the first token and grows it per delta. The transcript is server-owned —
// this component never sends history, only the new message.
//
// Phases: "chat" (the conversation) → "grading" (attempt_complete in flight)
// → "scorecard". A grading failure lands on "scorecard" with a retry notice
// (completion already stands server-side).

function RoleplayPlayerModal(props) {
  const { open, onClose, homeCompany, roleplayUuid, sectionUUID, language, onCompleted } = props;
  // Preview mode: test-drive the CURRENT (possibly unsaved) editor state via
  // the stateless preview endpoints. No attempt rows, no counts, no
  // completion — the transcript lives in this component (historyRef).
  const previewMode = !!props.previewMode;
  const previewScenario = props.previewScenario || null;

  const [phase, setPhase] = React.useState("chat");
  const [attempt, setAttempt] = React.useState(null); // start-endpoint payload
  const [messages, setMessages] = React.useState([]);
  const [inputValue, setInputValue] = React.useState("");
  const [isTyping, setIsTyping] = React.useState(false);
  const [isStarting, setIsStarting] = React.useState(false);
  const [conversationOver, setConversationOver] = React.useState(false);
  const [turnsUsed, setTurnsUsed] = React.useState(0);
  const [scorecard, setScorecard] = React.useState(null);
  const [gradingFailed, setGradingFailed] = React.useState(false);
  // The complete POST itself failed on a user-ended conversation (nothing was
  // saved server-side) — distinct from gradingFailed, where completion stands
  // and only the feedback is missing.
  const [completeFailed, setCompleteFailed] = React.useState(false);
  const [isEnding, setIsEnding] = React.useState(false);

  const scrollRef = React.useRef(null);
  const inputRef = React.useRef(null);
  // Guards the auto-complete call so done-events from a re-render can't
  // double-fire it (the endpoint is idempotent anyway; this is UX hygiene).
  const completeFiredRef = React.useRef(false);
  // Whether the server already marked the attempt completed (turn-stream
  // sentinel/max-turns endings) before the complete POST — decides how a
  // failed POST is reported (see runComplete).
  const serverCompletedRef = React.useRef(false);
  // Coalesce per-token scroll requests into one per animation frame — the
  // scrollHeight read forces a layout, so once per token thrashes.
  const scrollPendingRef = React.useRef(false);
  // Preview mode's client-held transcript (OpenAI-shaped) + how it ended —
  // the preview endpoints are stateless, so this is the source of truth.
  const historyRef = React.useRef([]);
  const endedReasonRef = React.useRef(null);

  // ---- Voice mode state ----
  // "idle" | "connecting" | "connected". While connected the text input is
  // replaced by the voice bar; switching back to typing keeps the transcript
  // (attempts sync turns server-side; preview holds them in historyRef).
  const [voiceState, setVoiceState] = React.useState("idle");
  const [voiceActivity, setVoiceActivity] = React.useState(VoiceActivityState.IDLE);
  const [voiceMuted, setVoiceMuted] = React.useState(false);
  const voiceServiceRef = React.useRef(null);
  // Trainee utterances finalized since the last persona reply — VAD can split
  // one thought into several transcripts, so they accumulate and flush as ONE
  // exchange when the persona's reply finalizes.
  const pendingUserRef = React.useRef([]);
  // Utterances belonging to the NEXT exchange: a transcript that arrives
  // while the current exchange already has its trainee words AND a reply in
  // flight is a barge-in (or quick follow-up), not more of the same turn —
  // joining it into pendingUserRef would fuse two turns into one saved
  // message ordered before the reply it interrupted. Promoted into
  // pendingUserRef when the current exchange flushes.
  const nextUserRef = React.useRef([]);
  // Streaming voice bubble for the persona's current reply.
  const voiceAiMsgIdRef = React.useRef(null);
  // The trainee-speech transcription is a SIDE pass that can finish after the
  // persona's reply (the model answers the audio directly). A finalized reply
  // whose trainee transcript hasn't arrived yet parks here ({text, bubbleId})
  // so the late transcript can be inserted ABOVE it and the exchange flushed
  // in the right order.
  const pendingAssistantRef = React.useRef(null);
  // Accumulated delta text of the persona reply currently streaming — so a
  // teardown mid-reply can flush the partial instead of losing it.
  const streamingAssistantRef = React.useRef("");
  // Placeholder user bubbles ({id, timer}), appended the moment speech STARTS
  // (VAD) so they sit at the chronologically correct spot, then filled in
  // place when the lagging transcript arrives — no visible reordering. The
  // timer reaps a placeholder whose transcript never comes (echo-filtered
  // noise).
  const placeholderQueueRef = React.useRef([]);
  // Persona reply text withheld from display (string) while the trainee's
  // transcript is still in flight — audio plays immediately; the bubble is
  // released once the trainee's words are on screen, guaranteeing strict
  // conversational order. null = nothing held.
  const heldAssistantRef = React.useRef(null);
  // Finalized exchange awaiting response.done before syncing (string of the
  // persona's final text; null = none). The flush waits those extra
  // milliseconds because only response.done reveals whether the persona
  // called end_scenario — a sentinel ending then completes the attempt
  // ATOMICALLY with the final sync (same rail as the turn cap), instead of
  // depending on a separate attempt_complete POST a tab close could lose.
  const readyExchangeRef = React.useRef(null);
  // True once the persona has called end_scenario — read by flushes that run
  // after response.done (the parked/late-transcript path).
  const scenarioEndingRef = React.useRef(false);
  // Fresh voice conversations re-SPEAK the authored opening line (that's the
  // instruction) — its text bubble already exists from the seed, so the first
  // assistant transcript is swallowed for display + sync.
  const swallowFirstAssistantRef = React.useRef(false);
  // Server-confirmed count captured per-flush for voice turn_index idempotency.
  const turnsUsedRef = React.useRef(0);
  React.useEffect(() => {
    turnsUsedRef.current = turnsUsed;
  }, [turnsUsed]);

  const voiceSupported =
    typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
  const voiceAvailable =
    voiceSupported && (previewMode ? true : !!attempt?.voice_enabled);

  const maxTurns = attempt?.max_turns || null;

  // Auto-grow the input with its content (up to ~5 lines, then scroll).
  // Runs on every inputValue change so it also shrinks back after send and
  // grows when a failed turn restores a long message.
  const autoGrowInput = React.useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  }, []);
  React.useEffect(() => {
    autoGrowInput();
  }, [inputValue, autoGrowInput]);

  const scrollToBottom = React.useCallback(() => {
    if (scrollPendingRef.current) return;
    scrollPendingRef.current = true;
    requestAnimationFrame(() => {
      scrollPendingRef.current = false;
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  const startAttempt = React.useCallback(async () => {
    setIsStarting(true);
    setPhase("chat");
    setMessages([]);
    setScorecard(null);
    setGradingFailed(false);
    setCompleteFailed(false);
    setConversationOver(false);
    setTurnsUsed(0);
    setIsTyping(false);
    setIsEnding(false);
    completeFiredRef.current = false;
    serverCompletedRef.current = false;
    endedReasonRef.current = null;
    stopVoice();

    if (previewMode) {
      // Local seed from the editor's current state — no server call.
      const opening = previewScenario?.opening_line || "";
      historyRef.current = opening
        ? [{ role: "assistant", content: opening }]
        : [];
      setAttempt({
        attempt_uuid: "preview",
        max_turns: previewScenario?.max_turns || 8,
        display_criteria: (previewScenario?.criteria || []).map((c, i) => ({
          id: String(i),
          label: c.label,
        })),
      });
      setMessages(
        opening ? [{ id: "seed_0", role: "assistant", content: opening }] : []
      );
      setIsStarting(false);
      scrollToBottom();
      setTimeout(() => inputRef.current?.focus(), 50);
      return;
    }

    try {
      const res = await postRightworkAPI(API_post_learning_roleplay_attempt_start, {
        company_id: homeCompany,
        learning_roleplay_uuid: roleplayUuid,
        section_uuid: sectionUUID,
        language: language || "english",
      });
      const payload = res.data?.redux?.payload || {};
      setAttempt(payload);
      setTurnsUsed(payload.turns_used || 0);
      setMessages(
        (payload.messages || []).map((m, i) => ({
          id: `seed_${i}`,
          role: m.role,
          content: m.content,
        }))
      );
      scrollToBottom();
      setTimeout(() => inputRef.current?.focus(), 50);
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message ||
          "Couldn't start this practice scenario. Please try again."
      );
      onClose();
    } finally {
      setIsStarting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeCompany, roleplayUuid, sectionUUID, language, previewMode, previewScenario]);

  React.useEffect(() => {
    if (open) startAttempt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Preview grading: stateless POST of the client-held transcript against the
  // editor's current criteria. Any failure reads as gradingFailed (there is no
  // completion to lose in preview).
  const runPreviewGrade = React.useCallback(async () => {
    setPhase("grading");
    try {
      const res = await postRightworkAPI(API_post_learning_roleplay_preview_grade, {
        company_id: homeCompany,
        persona: previewScenario?.persona || "",
        situation: previewScenario?.situation || "",
        behavioral_directives: previewScenario?.behavioral_directives || "",
        knowledge_context: previewScenario?.knowledge_context || "",
        language: language || "english",
        criteria: JSON.stringify(previewScenario?.criteria || []),
        messages: JSON.stringify(historyRef.current),
        ended_reason: endedReasonRef.current || "user_ended",
      });
      const card = res.data?.redux?.payload?.scorecard || null;
      setScorecard(card);
      setGradingFailed(!card);
    } catch (error) {
      setScorecard(null);
      setGradingFailed(true);
    } finally {
      setPhase("scorecard");
    }
  }, [homeCompany, previewScenario, language]);

  const runComplete = React.useCallback(
    async (attemptUuid, endedReason) => {
      setPhase("grading");
      setCompleteFailed(false);
      let succeeded = false;
      try {
        const res = await postRightworkAPI(API_post_learning_roleplay_attempt_complete, {
          company_id: homeCompany,
          attempt_uuid: attemptUuid,
          // Only meaningful when this POST performs the completion (voice
          // endings and End-conversation); text endings were already
          // committed by the turn stream.
          ended_reason: endedReason || "user_ended",
        });
        succeeded = true;
        serverCompletedRef.current = true;
        const payload = res.data?.redux?.payload?.attempt || {};
        setScorecard(payload.scorecard || null);
        setGradingFailed(!payload.scorecard);
      } catch (error) {
        setScorecard(null);
        if (serverCompletedRef.current) {
          // The conversation ended server-side (sentinel/max-turns commit) —
          // completion stands; only the feedback is missing.
          setGradingFailed(true);
        } else {
          // User-ended path: the failed POST was the ONLY thing that would
          // have completed the attempt. Nothing was saved — say so, and let
          // the retry button (or reopening the player, which resumes the
          // in_progress attempt) recover.
          setCompleteFailed(true);
          completeFiredRef.current = false;
        }
      } finally {
        setPhase("scorecard");
        // Refresh the section's can_complete state only when the attempt is
        // actually completed server-side.
        if ((succeeded || serverCompletedRef.current) && onCompleted) onCompleted();
      }
    },
    [homeCompany, onCompleted]
  );

  // ---- Voice mode ----

  // Assigned every render (flushVoiceExchange is defined below); stopVoice
  // reads it through the ref so its own identity stays stable — the
  // unmount-cleanup effect keys off that.
  const flushVoiceExchangeRef = React.useRef(null);

  // The most recent strand-flush. disconnect() can fire onScenarioEnd
  // reentrantly from inside stopVoice — the inner stopVoice finds no strands
  // left and must return THIS in-flight promise, not a fresh resolved one,
  // or grading would race the final sync.
  const lastVoiceFlushRef = React.useRef(Promise.resolve());

  // Returns a promise for the latest strand-flush, so endings can await the
  // sync before grading reads the transcript.
  const stopVoice = React.useCallback(() => {
    // Flush anything teardown would otherwise discard: a finalized exchange
    // still waiting on response.done, a parked persona reply whose trainee
    // transcript never arrived, or trainee speech still awaiting a reply
    // (paired with the partial streamed text, if any).
    const parked = pendingAssistantRef.current;
    pendingAssistantRef.current = null;
    const ready = readyExchangeRef.current;
    readyExchangeRef.current = null;
    let flushPromise = lastVoiceFlushRef.current;
    const flush = flushVoiceExchangeRef.current;
    if (flush) {
      // Barge-in speech awaiting the next exchange is captured up front —
      // the resets below run before any chained .then, so it can't be read
      // from the refs later.
      const bargedWords = nextUserRef.current;
      nextUserRef.current = [];
      if (ready !== null) {
        // response.done never arrived, so whether the persona ended the
        // scene is unknown — sync without a reason; a pending scenario end
        // still completes via disconnect's onScenarioEnd re-fire.
        flushPromise = flush(ready);
      } else if (pendingUserRef.current.length > 0) {
        flushPromise = flush(streamingAssistantRef.current);
      } else if (parked) {
        flushPromise = flush(parked.text);
      }
      if (bargedWords.length > 0) {
        // Trainee words the persona never answered still belong in the
        // transcript — their own user-only exchange, sequenced AFTER the
        // primary strand so the saved order matches the conversation.
        flushPromise = flushPromise.then(() => {
          pendingUserRef.current = bargedWords;
          return flushVoiceExchangeRef.current?.("");
        });
      }
    }
    streamingAssistantRef.current = "";
    // Reap unfilled speech placeholders — their transcripts will never
    // arrive once the connection is down.
    const orphaned = placeholderQueueRef.current;
    placeholderQueueRef.current = [];
    if (orphaned.length > 0) {
      orphaned.forEach((p) => clearTimeout(p.timer));
      const orphanIds = new Set(orphaned.map((p) => p.id));
      setMessages((prev) => prev.filter((m) => !orphanIds.has(m.id)));
    }
    // Null the ref BEFORE disconnecting: disconnect emits DISCONNECTED,
    // whose handler calls stopVoice again — the nulled ref breaks the cycle.
    const service = voiceServiceRef.current;
    voiceServiceRef.current = null;
    service?.disconnect();
    setVoiceState("idle");
    setVoiceActivity(VoiceActivityState.IDLE);
    setVoiceMuted(false);
    pendingUserRef.current = [];
    nextUserRef.current = [];
    voiceAiMsgIdRef.current = null;
    heldAssistantRef.current = null;
    scenarioEndingRef.current = false;
    return flushPromise;
  }, []);

  // Tear the WebRTC session down if the modal unmounts mid-conversation.
  React.useEffect(() => stopVoice, [stopVoice]);

  // One finished exchange (trainee speech + persona reply) → sync it. Either
  // side may be absent (but not both): teardown flushes strands — a persona
  // reply whose trainee transcript never arrived (assistant-only; the unheard
  // words are unrecoverable), or trainee speech the persona never answered.
  const flushVoiceExchange = React.useCallback(
    async (assistantFinalText, endedReason) => {
      const userText = pendingUserRef.current.join(" ").trim();
      // This exchange is consumed — barge-in speech parked for the next
      // exchange becomes the pending words of the new current one.
      pendingUserRef.current = nextUserRef.current;
      nextUserRef.current = [];
      const assistantText = (assistantFinalText || "").trim();
      if (!userText && !assistantText) return;
      // Assistant-only exchanges don't advance the trainee turn count.
      const nextTurnIndex = userText
        ? turnsUsedRef.current + 1
        : turnsUsedRef.current;
      if (previewMode) {
        historyRef.current = [
          ...historyRef.current,
          ...(userText ? [{ role: "user", content: userText }] : []),
          ...(assistantText
            ? [{ role: "assistant", content: assistantText }]
            : []),
        ];
        // Ref updates synchronously — the max-turns backstop reads it right
        // after this flush, before React re-renders.
        turnsUsedRef.current = nextTurnIndex;
        setTurnsUsed(nextTurnIndex);
        return;
      }
      // Client-minted idempotency key: the server records each exchange_id
      // at most once, so retrying a failed POST is always safe, and a
      // committed POST whose response was lost in transit can never make a
      // later NEW exchange look like a retry (which would silently drop it).
      const exchangeId = `vx_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      const postExchange = () =>
        postRightworkAPI(API_post_learning_roleplay_voice_turns, {
          company_id: homeCompany,
          attempt_uuid: attempt.attempt_uuid,
          exchange_id: exchangeId,
          user_text: userText,
          assistant_text: assistantText,
          // "sentinel" on the scenario-ending exchange — the server then
          // completes the attempt atomically with this sync.
          ended_reason: endedReason || "",
        });
      try {
        let res;
        try {
          res = await postExchange();
        } catch (firstError) {
          res = await postExchange(); // idempotent — safe to retry
        }
        const payload = res.data?.redux?.payload || {};
        const confirmed = payload.turns_used ?? nextTurnIndex;
        turnsUsedRef.current = confirmed;
        setTurnsUsed(confirmed);
        // The turn cap commits completion server-side, atomically with the
        // sync — record it so a failed follow-up grading POST is reported as
        // "feedback missing", not "nothing was saved".
        if (payload.status === "completed" || payload.status === "graded") {
          serverCompletedRef.current = true;
        }
      } catch (error) {
        // Both tries failed — but the first may still have COMMITTED with
        // its response lost in transit. Resync the authoritative state so
        // the turn display and max-turns backstop stay truthful either way.
        try {
          const res = await getRightworkAPI(API_get_learning_roleplay_attempt, {
            company_id: homeCompany,
            attempt_uuid: attempt.attempt_uuid,
          });
          const serverAttempt = res.data?.redux?.payload?.attempt || {};
          const serverTurns = serverAttempt.turns_used;
          if (
            serverAttempt.status === "completed" ||
            serverAttempt.status === "graded"
          ) {
            serverCompletedRef.current = true;
          }
          if (typeof serverTurns === "number") {
            turnsUsedRef.current = serverTurns;
            setTurnsUsed(serverTurns);
            if (serverTurns >= nextTurnIndex) return; // it landed after all
          }
        } catch (resyncError) {
          // Resync unavailable too — fall through to the warning.
        }
        toast.warn("A voice exchange didn't sync. It may be missing from feedback.");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previewMode, homeCompany, attempt]
  );
  // Every flush goes through this wrapper so lastVoiceFlushRef always holds
  // the latest in-flight sync — endings await it before grading.
  const runVoiceFlush = React.useCallback(
    (assistantFinalText, endedReason) => {
      const p = flushVoiceExchange(assistantFinalText, endedReason);
      lastVoiceFlushRef.current = p;
      return p;
    },
    [flushVoiceExchange]
  );
  flushVoiceExchangeRef.current = runVoiceFlush;

  // The trainee's final ASR can still be in flight when a conversation ends
  // (reply parked unpaired, or a committed utterance's placeholder unfilled).
  // Disconnecting kills the channel the transcript arrives on, so endings
  // wait — bounded — for it to land; arrival triggers the parked flush,
  // which pairs and syncs the full exchange before teardown proceeds.
  const waitForPendingVoiceTranscript = React.useCallback(() => {
    return new Promise((resolve) => {
      const start = Date.now();
      const check = () => {
        const stillWaiting =
          pendingAssistantRef.current !== null ||
          placeholderQueueRef.current.length > 0;
        if (!stillWaiting || Date.now() - start > 5000) {
          resolve();
          return;
        }
        setTimeout(check, 150);
      };
      check();
    });
  }, []);

  const endVoiceConversation = React.useCallback(
    async (endedReason) => {
      setConversationOver(true);
      await waitForPendingVoiceTranscript();
      const flushed = stopVoice();
      endedReasonRef.current = endedReason;
      if (!completeFiredRef.current) {
        completeFiredRef.current = true;
        // Grading reads the transcript — let any teardown strand-flush land
        // first (flush failures already warned; grade what did sync).
        await flushed.catch(() => {});
        if (previewMode) {
          runPreviewGrade();
        } else {
          // serverCompletedRef stays as the flush left it: true when the
          // turn cap already committed completion server-side, false when
          // this POST is what completes (sentinel endings).
          runComplete(attempt.attempt_uuid, endedReason);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previewMode, attempt, runComplete, runPreviewGrade, stopVoice, waitForPendingVoiceTranscript]
  );

  const startVoice = React.useCallback(() => {
    if (voiceState !== "idle" || conversationOver) return;
    setVoiceState("connecting");
    // Fresh conversation (no trainee turns yet): the persona re-speaks the
    // authored opening line, whose bubble already exists — swallow its
    // transcript for display and sync.
    const isFresh = turnsUsedRef.current === 0;
    swallowFirstAssistantRef.current = isFresh;
    streamingAssistantRef.current = "";
    readyExchangeRef.current = null;
    scenarioEndingRef.current = false;
    // The authored opening line (learner-visible; its bubble is already
    // rendered) — passed so the first spoken response is verbatim.
    const openingLine = previewMode
      ? previewScenario?.opening_line || ""
      : messages.find((m) => m.role === "assistant")?.content || "";

    const service = new RoleplayRealtimeService({
      onConnectionStateChange: (state) => {
        if (state === VoiceConnectionState.CONNECTED) setVoiceState("connected");
        if (
          state === VoiceConnectionState.DISCONNECTED ||
          state === VoiceConnectionState.ERROR
        ) {
          // Service-initiated disconnects (idle timeout, ICE loss) must run
          // the same teardown as user-initiated ones — stopVoice flushes
          // stranded exchanges and clears the pairing refs so a later voice
          // restart can't mispair them. Reentrancy from player-initiated
          // stops is safe: strands were already consumed, so this no-ops.
          stopVoice();
        }
      },
      onActivityStateChange: setVoiceActivity,
      onSpeechStopped: () => {
        // Drop the trainee's bubble the moment their utterance commits — the
        // transcript fills it in place later, so the persona's reply
        // rendering first never forces a visible reorder. (Same pattern and
        // timing as the KB voice assistant.)
        const id = `vp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
        const timer = setTimeout(() => {
          // Transcript never arrived (echo-filtered noise) — reap it.
          placeholderQueueRef.current = placeholderQueueRef.current.filter(
            (p) => p.id !== id
          );
          setMessages((prev) => prev.filter((m) => m.id !== id));
          if (placeholderQueueRef.current.length > 0) return;
          // Nothing left to wait for — release a reply held back for this
          // transcript so it doesn't stay invisible.
          if (heldAssistantRef.current !== null) {
            const heldText = heldAssistantRef.current;
            heldAssistantRef.current = null;
            const hid = `va_${Date.now()}`;
            voiceAiMsgIdRef.current = hid;
            setMessages((prev) => [
              ...prev,
              { id: hid, role: "assistant", content: heldText },
            ]);
            scrollToBottom();
          } else if (
            pendingAssistantRef.current &&
            pendingUserRef.current.length === 0
          ) {
            // Finished reply parked for these words — render it and sync it
            // assistant-only (the unheard trainee words are unrecoverable).
            const parked = pendingAssistantRef.current;
            pendingAssistantRef.current = null;
            if (parked.text) {
              setMessages((prev) => [
                ...prev,
                { id: `va_${Date.now()}`, role: "assistant", content: parked.text },
              ]);
              scrollToBottom();
            }
            runVoiceFlush(
              parked.text,
              scenarioEndingRef.current ? "sentinel" : undefined
            );
          }
        }, 20000);
        placeholderQueueRef.current.push({ id, timer });
        setMessages((prev) => [
          ...prev,
          { id, role: "user", content: "", pending: true },
        ]);
        scrollToBottom();
      },
      onUserTranscript: (text) => {
        // Which exchange does this transcript belong to? If the current
        // exchange already has its trainee words AND a persona reply owns it
        // (streaming, held, finalized awaiting response.done, or parked),
        // this is barge-in/follow-up speech for the NEXT exchange — fusing
        // it into pendingUserRef would join two turns into one message.
        const replyOwnsExchange =
          !!voiceAiMsgIdRef.current ||
          heldAssistantRef.current !== null ||
          readyExchangeRef.current !== null ||
          !!pendingAssistantRef.current;
        const belongsToNext =
          replyOwnsExchange && pendingUserRef.current.length > 0;
        const targetRef = belongsToNext ? nextUserRef : pendingUserRef;
        // Fill the oldest awaiting placeholder in place; it already sits at
        // the right spot (appended when the speech began).
        const placeholder = placeholderQueueRef.current.shift();
        if (placeholder) {
          clearTimeout(placeholder.timer);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === placeholder.id
                ? { ...m, content: text, pending: false }
                : m
            )
          );
          targetRef.current.push(text);
          // The trainee's words are on screen — release a reply whose text
          // was held back for them (streaming continues into the bubble).
          // (Only for current-exchange transcripts: a barge-in doesn't
          // unlock the reply it interrupted.)
          if (!belongsToNext && heldAssistantRef.current !== null && placeholderQueueRef.current.length === 0) {
            const heldText = heldAssistantRef.current;
            heldAssistantRef.current = null;
            const id = `va_${Date.now()}`;
            voiceAiMsgIdRef.current = id;
            setMessages((prev) => [
              ...prev,
              { id, role: "assistant", content: heldText },
            ]);
          }
        } else {
          // No placeholder (shouldn't normally happen) — fall back to
          // order-aware insertion: a late transcript for an exchange whose
          // reply is already rendering belongs ABOVE that reply.
          const msg = {
            id: `vu_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
            role: "user",
            content: text,
          };
          const isLateForExchange = pendingUserRef.current.length === 0;
          const anchorId = isLateForExchange
            ? voiceAiMsgIdRef.current || pendingAssistantRef.current?.bubbleId
            : null;
          setMessages((prev) => {
            if (anchorId) {
              const idx = prev.findIndex((m) => m.id === anchorId);
              if (idx !== -1) {
                return [...prev.slice(0, idx), msg, ...prev.slice(idx)];
              }
            }
            return [...prev, msg];
          });
          targetRef.current.push(text);
        }
        // If the reply already finalized while waiting for this transcript,
        // the exchange is now complete — flush it. (Not for barge-in
        // transcripts: they belong to the NEXT exchange, and a parked reply
        // with belongsToNext true is contradictory anyway — parking requires
        // pendingUser to have been empty.)
        if (!belongsToNext && pendingAssistantRef.current) {
          const parked = pendingAssistantRef.current;
          pendingAssistantRef.current = null;
          // A reply parked while HELD has no bubble yet — render it now,
          // below the trainee's just-filled words.
          if (!parked.bubbleId && parked.text) {
            setMessages((prev) => [
              ...prev,
              { id: `va_${Date.now()}`, role: "assistant", content: parked.text },
            ]);
          }
          // Parked flushes run after response.done, so the sentinel flag is
          // already known via scenarioEndingRef.
          runVoiceFlush(
            parked.text,
            scenarioEndingRef.current ? "sentinel" : undefined
          ).then(() => {
            const max = attempt?.max_turns || previewScenario?.max_turns || 8;
            if (turnsUsedRef.current >= max && !completeFiredRef.current) {
              endVoiceConversation("max_turns");
            }
          });
        }
        scrollToBottom();
      },
      onAssistantTranscript: (text, isFinal) => {
        if (swallowFirstAssistantRef.current) {
          if (isFinal) swallowFirstAssistantRef.current = false;
          return;
        }
        // Mirror the in-flight reply so a teardown mid-stream can flush the
        // partial text instead of losing the exchange.
        streamingAssistantRef.current = isFinal
          ? ""
          : streamingAssistantRef.current + text;
        if (!isFinal) {
          // HOLD the reply's text while the trainee's transcript is still in
          // flight (their placeholder is unfilled): the audio plays
          // immediately, but the bubble waits so it always renders BELOW the
          // trainee's words. Released by the placeholder fill (or its reap).
          if (
            heldAssistantRef.current !== null ||
            (placeholderQueueRef.current.length > 0 && !voiceAiMsgIdRef.current)
          ) {
            heldAssistantRef.current = (heldAssistantRef.current || "") + text;
            return;
          }
          if (!voiceAiMsgIdRef.current) {
            const id = `va_${Date.now()}`;
            voiceAiMsgIdRef.current = id;
            setMessages((prev) => [
              ...prev,
              { id, role: "assistant", content: text },
            ]);
          } else {
            const id = voiceAiMsgIdRef.current;
            setMessages((prev) =>
              prev.map((m) => (m.id === id ? { ...m, content: m.content + text } : m))
            );
          }
          scrollToBottom();
          return;
        }
        // Final while still held: park text-only — the bubble is created
        // when the trainee's transcript lands (or the reap/teardown paths
        // resolve it).
        if (heldAssistantRef.current !== null && pendingUserRef.current.length === 0) {
          heldAssistantRef.current = null;
          pendingAssistantRef.current = { text: text.trim(), bubbleId: null };
          return;
        }
        heldAssistantRef.current = null;
        // Final: replace the streamed bubble with the authoritative transcript
        // (deltas can drift).
        let id = voiceAiMsgIdRef.current;
        voiceAiMsgIdRef.current = null;
        if (id) {
          setMessages((prev) =>
            prev.map((m) => (m.id === id ? { ...m, content: text } : m))
          );
        } else if (text.trim()) {
          id = `va_${Date.now()}`;
          setMessages((prev) => [
            ...prev,
            { id, role: "assistant", content: text },
          ]);
        }
        scrollToBottom();
        if (pendingUserRef.current.length === 0) {
          // The trainee's transcription hasn't arrived yet (it's a lagging
          // side pass) — park this reply; onUserTranscript pairs and flushes
          // when the words land, keeping bubble order and sync correct.
          pendingAssistantRef.current = { text: text.trim(), bubbleId: id };
          return;
        }
        // Don't sync yet: response.done (milliseconds away) reveals whether
        // this reply ended the scenario, letting the flush complete the
        // attempt atomically when it did.
        readyExchangeRef.current = text.trim();
      },
      onResponseDone: (scenarioEnding) => {
        if (scenarioEnding) scenarioEndingRef.current = true;
        const ready = readyExchangeRef.current;
        if (ready === null) return; // opening line, or transcript still in flight (parked path)
        readyExchangeRef.current = null;
        runVoiceFlush(ready, scenarioEnding ? "sentinel" : undefined).then(() => {
          // Client-side backstop: the persona is instructed to end_scenario
          // near the cap, but enforce it regardless.
          const max = attempt?.max_turns || previewScenario?.max_turns || 8;
          if (turnsUsedRef.current >= max && !completeFiredRef.current) {
            endVoiceConversation("max_turns");
          }
        });
      },
      onScenarioEnd: () => endVoiceConversation("sentinel"),
      onError: (message) => {
        toast.error(message);
        stopVoice();
      },
      onWarning: (message) => toast.info(message),
    });
    voiceServiceRef.current = service;

    service.connect(
      previewMode
        ? {
            sessionUrl: API_post_learning_roleplay_voice_preview_session,
            sessionParams: {
              company_id: homeCompany,
              persona: previewScenario?.persona || "",
              situation: previewScenario?.situation || "",
              behavioral_directives: previewScenario?.behavioral_directives || "",
              knowledge_context: previewScenario?.knowledge_context || "",
              opening_line: previewScenario?.opening_line || "",
              language: language || "english",
              max_turns: previewScenario?.max_turns || 8,
              voice: previewScenario?.voice || "",
              messages: JSON.stringify(historyRef.current),
            },
            speakFirst: isFresh,
            openingLine,
          }
        : {
            sessionUrl: API_post_learning_roleplay_voice_session,
            sessionParams: {
              company_id: homeCompany,
              attempt_uuid: attempt?.attempt_uuid,
            },
            speakFirst: isFresh,
            openingLine,
          }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceState, conversationOver, previewMode, previewScenario, attempt, homeCompany, language]);

  const toggleVoiceMute = () => {
    const next = !voiceMuted;
    setVoiceMuted(next);
    voiceServiceRef.current?.setMuted(next);
  };

  const handleSend = async () => {
    const text = inputValue.trim();
    if (!text || isTyping || conversationOver || !attempt?.attempt_uuid) return;

    setInputValue("");
    setIsTyping(true);
    const userMsgId = `u_${Date.now()}`;
    setMessages((prev) => [...prev, { id: userMsgId, role: "user", content: text }]);
    scrollToBottom();

    const aiMsgId = `a_${Date.now()}`;
    let aiStarted = false;
    let assistantText = "";

    if (previewMode) {
      // Stateless preview turn: send the client-held transcript with the
      // editor's current persona fields; commit both messages to historyRef
      // only after a successful done.
      try {
        await streamRightworkAPI(
          API_post_learning_roleplay_preview_turn_stream,
          {
            company_id: homeCompany,
            persona: previewScenario?.persona || "",
            situation: previewScenario?.situation || "",
            behavioral_directives: previewScenario?.behavioral_directives || "",
            knowledge_context: previewScenario?.knowledge_context || "",
            language: language || "english",
            max_turns: previewScenario?.max_turns || 8,
            messages: JSON.stringify(historyRef.current),
            message: text,
          },
          (token) => {
            assistantText += token;
            if (!aiStarted) {
              aiStarted = true;
              setMessages((prev) => [
                ...prev,
                { id: aiMsgId, role: "assistant", content: token },
              ]);
            } else {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === aiMsgId ? { ...m, content: m.content + token } : m
                )
              );
            }
            scrollToBottom();
          },
          (done) => {
            historyRef.current = [
              ...historyRef.current,
              { role: "user", content: text },
              ...(assistantText.trim()
                ? [{ role: "assistant", content: assistantText.trim() }]
                : []),
            ];
            setTurnsUsed(done.turns_used || 0);
            if (done.conversation_over) {
              endedReasonRef.current = done.end_reason || "max_turns";
              setConversationOver(true);
              if (!completeFiredRef.current) {
                completeFiredRef.current = true;
                runPreviewGrade();
              }
            }
          },
          () => {}
        );
      } catch (error) {
        // Nothing is persisted anywhere in preview — plain rollback is exact.
        setMessages((prev) => prev.filter((m) => m.id !== userMsgId && m.id !== aiMsgId));
        setInputValue(text);
        toast.error(error?.message || "The preview hit a problem. Try sending again.");
      } finally {
        setIsTyping(false);
        scrollToBottom();
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      return;
    }

    try {
      await streamRightworkAPI(
        API_post_learning_roleplay_turn_stream,
        {
          company_id: homeCompany,
          attempt_uuid: attempt.attempt_uuid,
          message: text,
        },
        (token) => {
          if (!aiStarted) {
            aiStarted = true;
            setMessages((prev) => [
              ...prev,
              { id: aiMsgId, role: "assistant", content: token },
            ]);
          } else {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === aiMsgId ? { ...m, content: m.content + token } : m
              )
            );
          }
          scrollToBottom();
        },
        (done) => {
          setTurnsUsed(done.turns_used || 0);
          if (done.conversation_over) {
            // The turn stream already committed the completed status.
            serverCompletedRef.current = true;
            setConversationOver(true);
            if (!completeFiredRef.current) {
              completeFiredRef.current = true;
              runComplete(attempt.attempt_uuid);
            }
          }
        },
        () => {}
      );
    } catch (error) {
      // The turn may or may not have been persisted — the stream can drop
      // AFTER the server committed (e.g. the done frame is lost in transit).
      // Resync from the server-authoritative transcript instead of guessing;
      // a blind rollback would invite a re-send that duplicates the exchange.
      let resynced = false;
      try {
        const res = await getRightworkAPI(API_get_learning_roleplay_attempt, {
          company_id: homeCompany,
          attempt_uuid: attempt.attempt_uuid,
        });
        const serverAttempt = res.data?.redux?.payload?.attempt;
        if (serverAttempt) {
          resynced = true;
          const serverMessages = serverAttempt.messages || [];
          setMessages(
            serverMessages.map((m, i) => ({
              id: `sync_${Date.now()}_${i}`,
              role: m.role,
              content: m.content,
            }))
          );
          setTurnsUsed(serverAttempt.turns_used || 0);
          // Committed-detection by TURN COUNT, not content: `turnsUsed` holds
          // the server-confirmed count before this send, so a higher count
          // means this turn landed. Content comparison would false-positive
          // on repeated short replies ("ok") matching an earlier message, and
          // false-negative when server-side sanitization altered the text.
          const committed = (serverAttempt.turns_used || 0) > turnsUsed;
          if (!committed) {
            setInputValue(text);
            toast.error("That didn't go through. Try sending again.");
          }
          if (
            serverAttempt.status === "completed" ||
            serverAttempt.status === "graded"
          ) {
            serverCompletedRef.current = true;
            setConversationOver(true);
            if (!completeFiredRef.current) {
              completeFiredRef.current = true;
              runComplete(attempt.attempt_uuid);
            }
          }
        }
      } catch (resyncError) {
        // Resync unavailable too — fall through to the optimistic rollback.
      }
      if (!resynced) {
        setMessages((prev) => prev.filter((m) => m.id !== userMsgId && m.id !== aiMsgId));
        setInputValue(text);
        toast.error(error?.message || "The conversation hit a problem. Try sending again.");
      }
    } finally {
      setIsTyping(false);
      scrollToBottom();
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleEndConversation = async () => {
    if (isTyping || isEnding || !attempt?.attempt_uuid) return;
    // Backend requires at least one learner message before ending.
    if (turnsUsed === 0) {
      toast.info("Say at least one thing to the character before ending.");
      return;
    }
    setIsEnding(true);
    setConversationOver(true);
    // A just-spoken final utterance's ASR may still be in flight (people
    // often speak, then immediately click End) — give it a bounded window
    // before the disconnect closes the channel it arrives on.
    if (voiceState !== "idle") {
      await waitForPendingVoiceTranscript();
    }
    const flushed = stopVoice();
    if (!completeFiredRef.current) {
      completeFiredRef.current = true;
      // Grading reads the transcript — let any teardown strand-flush land
      // first.
      await flushed.catch(() => {});
      if (previewMode) {
        endedReasonRef.current = "user_ended";
        await runPreviewGrade();
      } else {
        await runComplete(attempt.attempt_uuid, "user_ended");
      }
    }
    setIsEnding(false);
  };

  const handleRetryGrading = () => {
    if (previewMode) {
      runPreviewGrade();
      return;
    }
    if (attempt?.attempt_uuid) runComplete(attempt.attempt_uuid);
  };

  const displayCriteria = attempt?.display_criteria || [];
  // Cover image: from the attempt-start payload for real attempts, or the
  // editor's previewScenario in preview mode.
  const coverImage = attempt?.cover_image || previewScenario?.cover_image || null;

  return (
    <Modal open={open} onClose={phase === "grading" ? undefined : onClose}>
      <Box sx={style}>
        {/* Header */}
        <div className="border-b border-gray-200 px-5 py-3.5 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            {coverImage ? (
              <img
                src={coverImage}
                alt=""
                className="h-8 w-8 shrink-0 rounded-lg object-cover"
              />
            ) : (
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-100">
                <ConnectWithoutContactIcon className="text-violet-600" style={{ fontSize: 18 }} />
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <div className="text-sm font-bold text-slate-900 truncate">
                  {props.roleplayName || previewScenario?.name || "Practice Scenario"}
                </div>
                {previewMode && (
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 font-medium rounded text-xs shrink-0">
                    Preview
                  </span>
                )}
              </div>
              {phase === "chat" && maxTurns && (
                <div className="text-xs text-slate-500">
                  Reply {Math.min(turnsUsed + 1, maxTurns)} of {maxTurns}
                </div>
              )}
            </div>
          </div>
          <button
            className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 disabled:opacity-40"
            onClick={onClose}
            disabled={phase === "grading"}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        {isStarting ? (
          <div className="flex-1 flex items-center justify-center">
            <LoadSpinner />
          </div>
        ) : phase === "scorecard" ? (
          <>
          <div className="flex-1 overflow-y-auto px-5 py-5">
            <div className="text-center mb-5">
              <div className="text-lg font-bold text-slate-900">
                {completeFailed ? "Almost done" : "Conversation complete"}
              </div>
              <p className="text-sm text-slate-600 mt-1">
                {completeFailed
                  ? "One more step to wrap up this conversation."
                  : "Here's how you did. This is coaching, not a grade."}
              </p>
            </div>
            {completeFailed ? (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-center">
                <p className="text-sm text-amber-800">
                  We couldn't save the end of your conversation. Check your
                  connection and try again. Your messages are safe.
                </p>
                <button
                  type="button"
                  onClick={handleRetryGrading}
                  className="mt-3 px-4 py-2 rounded-lg text-sm font-medium text-white bg-violet-600 hover:bg-violet-700"
                >
                  Finish conversation
                </button>
              </div>
            ) : gradingFailed ? (
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-center">
                <p className="text-sm text-slate-700">
                  {previewMode
                    ? "Feedback couldn't be generated for this preview run."
                    : "Your conversation was saved and this section counts as done. Feedback is temporarily unavailable."}
                </p>
                <button
                  type="button"
                  onClick={handleRetryGrading}
                  className="mt-3 px-4 py-2 rounded-lg text-sm font-medium text-violet-700 border border-violet-200 hover:bg-violet-50"
                >
                  Try loading feedback again
                </button>
              </div>
            ) : (
              <RoleplayScorecard scorecard={scorecard} />
            )}
          </div>
          {/* Sticky footer — actions stay visible while the scorecard scrolls,
              matching the other modals' footer pattern. */}
          <div className="border-t border-gray-200 bg-white px-5 py-3.5 shrink-0 flex items-center justify-center gap-3">
            {/* While the completion save is pending retry, "Try again" would
                just resume this same unfinished attempt — hide it to avoid
                competing with the "Finish conversation" retry above. */}
            {!completeFailed && (
              <button
                type="button"
                onClick={startAttempt}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-violet-700 border border-violet-200 hover:bg-violet-50"
              >
                <ReplayIcon style={{ fontSize: 18 }} />
                Try again
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 rounded-lg text-sm font-medium text-white bg-violet-600 hover:bg-violet-700"
            >
              {completeFailed ? "Close" : "Done"}
            </button>
          </div>
        </>
        ) : (
          <>
            {/* Cover hero — intro state only, sets the scene before the chat */}
            {coverImage && messages.length <= 1 && (
              <div className="px-5 pt-4 shrink-0">
                <div className="aspect-[5/2] w-full overflow-hidden rounded-lg bg-slate-100">
                  <img src={coverImage} alt="" className="h-full w-full object-cover" />
                </div>
              </div>
            )}

            {/* What you'll be coached on */}
            {displayCriteria.length > 0 && messages.length <= 1 && (
              <div className="px-5 pt-4 shrink-0">
                <div className="rounded-lg bg-violet-50 border border-violet-100 px-3.5 py-2.5">
                  <div className="text-xs font-semibold text-violet-700 uppercase tracking-wide mb-1">
                    You'll be coached on
                  </div>
                  <ul className="text-xs text-slate-700 space-y-0.5">
                    {displayCriteria.map((c) => (
                      <li key={c.id}>• {c.label}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {messages.map((m) => (
                <div
                  key={m.id}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-violet-600 text-white rounded-br-sm"
                        : "bg-slate-100 text-slate-900 rounded-bl-sm"
                    }`}
                  >
                    {m.pending && !m.content ? (
                      <span className="inline-flex items-center gap-1 py-1">
                        <span className="w-1.5 h-1.5 bg-white/80 rounded-full animate-bounce" />
                        <span
                          className="w-1.5 h-1.5 bg-white/80 rounded-full animate-bounce"
                          style={{ animationDelay: "0.12s" }}
                        />
                        <span
                          className="w-1.5 h-1.5 bg-white/80 rounded-full animate-bounce"
                          style={{ animationDelay: "0.24s" }}
                        />
                      </span>
                    ) : (
                      m.content
                    )}
                  </div>
                </div>
              ))}
              {/* Typing dots only while waiting for the first token — once the
                  reply bubble starts streaming, the growing bubble IS the
                  indicator. */}
              {isTyping && messages[messages.length - 1]?.role === "user" && (
                <div className="flex justify-start">
                  <div className="bg-slate-100 rounded-2xl rounded-bl-sm px-3.5 py-2.5">
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "120ms" }} />
                      <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "240ms" }} />
                    </div>
                  </div>
                </div>
              )}
              {phase === "grading" && (
                <div className="flex flex-col items-center py-6 gap-2">
                  <LoadSpinner />
                  <p className="text-sm text-slate-600">Reviewing your conversation…</p>
                </div>
              )}
            </div>

            {/* Input / voice bar */}
            <div className="border-t border-gray-200 px-4 py-3 shrink-0">
              {voiceState !== "idle" ? (
                <div className="flex items-center gap-3 rounded-xl border border-violet-200 bg-violet-50 px-4 py-2.5">
                  {voiceState === "connecting" ? (
                    <>
                      <LoadSpinner />
                      <span className="text-sm text-slate-700 flex-1">
                        Connecting voice…
                      </span>
                    </>
                  ) : (
                    <>
                      <span
                        className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                          voiceActivity === VoiceActivityState.SPEAKING
                            ? "bg-blue-500 animate-pulse"
                            : voiceActivity === VoiceActivityState.THINKING
                            ? "bg-amber-500 animate-pulse"
                            : "bg-emerald-500 animate-pulse"
                        }`}
                      />
                      <span className="text-sm text-slate-700 flex-1">
                        {voiceActivity === VoiceActivityState.SPEAKING
                          ? "Speaking…"
                          : voiceActivity === VoiceActivityState.THINKING
                          ? "Thinking…"
                          : voiceMuted
                          ? "Muted"
                          : "Listening…"}
                      </span>
                      <button
                        type="button"
                        onClick={toggleVoiceMute}
                        title={voiceMuted ? "Unmute" : "Mute"}
                        className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                          voiceMuted
                            ? "bg-rose-100 text-rose-600 hover:bg-rose-200"
                            : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
                        }`}
                      >
                        {voiceMuted ? (
                          <MicOffIcon style={{ fontSize: 18 }} />
                        ) : (
                          <MicIcon style={{ fontSize: 18 }} />
                        )}
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={stopVoice}
                    title="Switch to typing"
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 bg-white border border-slate-200 hover:bg-slate-100 transition-colors"
                  >
                    <KeyboardIcon style={{ fontSize: 16 }} />
                    Type
                  </button>
                </div>
              ) : (
                <div className="flex items-end gap-2">
                  <textarea
                    ref={inputRef}
                    rows={1}
                    style={{ maxHeight: 140, overflowY: "auto" }}
                    className="flex-1 resize-none rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-100 disabled:bg-slate-50 disabled:text-slate-400"
                    placeholder={
                      conversationOver ? "The conversation has ended" : "Type your reply…"
                    }
                    value={inputValue}
                    disabled={isTyping || conversationOver || phase === "grading"}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                  />
                  {voiceAvailable && !conversationOver && (
                    <button
                      type="button"
                      onClick={startVoice}
                      disabled={isTyping || phase === "grading"}
                      title="Speak to them"
                      className="w-10 h-10 shrink-0 rounded-xl flex items-center justify-center text-violet-700 border border-violet-200 bg-white hover:bg-violet-50 disabled:opacity-40 transition-colors"
                    >
                      <MicIcon style={{ fontSize: 18 }} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleSend}
                    disabled={!inputValue.trim() || isTyping || conversationOver}
                    className={`w-10 h-10 shrink-0 rounded-xl flex items-center justify-center ${
                      inputValue.trim() && !isTyping && !conversationOver
                        ? "bg-violet-600 hover:bg-violet-700 text-white"
                        : "bg-gray-200 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    <SendIcon style={{ fontSize: 18 }} />
                  </button>
                </div>
              )}
              {!conversationOver && (
                <div className="flex justify-center mt-2.5">
                  <button
                    type="button"
                    onClick={handleEndConversation}
                    disabled={isTyping || isEnding || turnsUsed === 0}
                    title={
                      turnsUsed === 0
                        ? "Say at least one thing to the character first"
                        : "Finish the conversation and get your coaching feedback"
                    }
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border border-slate-200 bg-white text-slate-700 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-700 transition-colors"
                  >
                    <SportsScoreIcon style={{ fontSize: 17 }} />
                    {isEnding ? "Ending…" : "End & get feedback"}
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </Box>
    </Modal>
  );
}

export default RoleplayPlayerModal;
